import { GAMING_DAY_TIMEZONE } from '@dayon/contracts';
import type { RestrictionRow } from './types.ts';

const DAY = new Intl.DateTimeFormat('en-PH', { day: '2-digit', month: 'short', year: 'numeric' });
export const CLOCK = new Intl.DateTimeFormat('en-PH', { hour: '2-digit', minute: '2-digit' });

/** "29 Aug 2026, 21:14" — for a ledger row, where the hour matters. */
export const STAMP = new Intl.DateTimeFormat('en-PH', {
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

/**
 * STAMP on Manila's clock, whatever the viewer's computer is set to. The Logs and Security tabs use
 * it because their filters are Manila days and their exported file is in Manila time: an entry at
 * 00:30 on the 30th in Manila must not read as the 29th on a laptop in another zone. STAMP itself
 * is left alone, because every other screen still reads it.
 */
export const MANILA_STAMP = new Intl.DateTimeFormat('en-PH', {
  timeZone: GAMING_DAY_TIMEZONE,
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

export const formatManilaMoment = (iso: string): string => MANILA_STAMP.format(new Date(iso));

export const formatDay = (iso: string): string => DAY.format(new Date(iso));

/**
 * A calendar day, `YYYY-MM-DD`, written as `formatDay` writes an instant — "May 14, 1990" — without
 * passing through the browser's clock: pinned to UTC midnight and printed in UTC, so it cannot move a
 * day either way.
 */
const CALENDAR_DAY = new Intl.DateTimeFormat('en-PH', {
  timeZone: 'UTC',
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});
export const formatCalendarDay = (ymd: string): string =>
  CALENDAR_DAY.format(new Date(`${ymd}T00:00:00Z`));

/**
 * The date beside On or Off, when the row carries one.
 *
 * Off is two different facts and an operator has to be able to tell them
 * apart: a control an operator TOOK OFF has a lifted_at, and one that simply
 * ran out has an expiry in the past and no decision behind it. Null when the
 * row says neither — a control lifted before lifted_at existed keeps its
 * honest silence rather than borrowing a date from somewhere else.
 */
export function stateDetail(record: RestrictionRow | undefined): string | null {
  if (!record) return null;
  if (record.in_force) {
    return record.expires_at ? `until ${formatDay(record.expires_at)}` : 'no expiry';
  }
  if (record.lifted_at) return `lifted ${formatDay(record.lifted_at)}`;
  if (record.active && record.expires_at) return `expired ${formatDay(record.expires_at)}`;
  return null;
}

/** Initials for the hero avatar. Two letters, first and last name part. */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '?';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}

/** A date input default: `days` from today, as YYYY-MM-DD. */
export function inDays(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Inline note validation: shown once the operator has started, never before. */
export const tooShort = (value: string): string | undefined =>
  value.length > 0 && value.trim().length < 10
    ? 'At least 10 characters — a code on its own reads as a form number.'
    : undefined;

/**
 * Points are a COUNT, not money.
 *
 * They cross the wire as a string for the same reason centavos do — the column
 * is a bigint — but they are never formatted as pesos. points_ledger carries no
 * rate to a peso value anywhere in this schema, and printing "₱1,824" beside a
 * points balance would invent one.
 */
export const formatPoints = (value: string): string => {
  const n = BigInt(value);
  const sign = n < 0n ? '−' : '';
  return `${sign}${(n < 0n ? -n : n).toLocaleString('en-PH')}`;
};
